import DealersApi from '@/api/dealers';
import DealerModel from '@/models/dealer';

const getInitialParams = () => ({
  skip: 0,
  limit: 100,
  only_deleted: false,
  order: {
    column: 'title',
    desc: true,
  },
});

export default {
  namespaced: true,

  state: {
    data: [],
    meta: {},
    cities: [],
    statuses: [],
    sources: [],
    loading: false,
    error: null,
    params: getInitialParams(),
    currentItem: null,
  },

  actions: {
    async fetch(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.fetch(ctx.getters.params);

        if (!response.cancelled) {
          ctx.commit('setData', response.data.data.items);
          ctx.commit('setMeta', response.data.meta);
          ctx.commit('setLoading', false);
        }
      } catch (err) {
        ctx.commit('setError', err);
        ctx.commit('setLoading', false);
      }
    },

    async getConfig(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.getConfig();
        ctx.commit('setCities', response.data.data.cities);
        ctx.commit('setStatuses', response.data.data.statuses);
        ctx.commit('setSources', response.data.data.sources);
        ctx.commit('setMeta', response.data.meta);
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async fetchOne(ctx, id) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.fetchOne(id);
        ctx.commit('setCurrentItem', new DealerModel(response.data.data));
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async save(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.update(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async remove(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        await DealersApi.remove(data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async create(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.create(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async sync(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.sync();
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async citiesCreate(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.createCity(data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        await ctx.dispatch('getConfig');
        ctx.commit('setLoading', false);
      }
    },

    async citiesSave(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.updateCity(data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        await ctx.dispatch('getConfig');
        ctx.commit('setLoading', false);
      }
    },

    async citiesRemove(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await DealersApi.removeCity(data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        const conflict = err.response && err.response.status === 409;
        return { err, conflict };
      } finally {
        await ctx.dispatch('getConfig');
        ctx.commit('setLoading', false);
      }
    },

    async changeParams(ctx, params, reset) {
      if (reset) {
        ctx.commit('changeParams', {
          ...getInitialParams(),
          ...params,
        });
      } else {
        ctx.commit('changeParams', params);
      }
    },

    clearCurrentItem(ctx) {
      ctx.commit('clearCurrentItem');
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },

    setError(state, err) {
      state.error = err;
    },

    setData(state, data) {
      state.data = data;
    },

    setMeta(state, meta) {
      state.meta = meta;
    },

    setCities(state, data) {
      state.cities = data;
    },

    setStatuses(state, data) {
      state.statuses = data;
    },

    setSources(state, data) {
      state.sources = data;
    },

    changeParams(state, params) {
      Object.assign(state.params, params);
    },

    setCurrentItem(state, data) {
      state.currentItem = data;
    },

    clearCurrentItem(state, data) {
      state.currentItem = null;
    },
  },

  getters: {
    meta: state => state.meta,
    list: state => state.data,
    cities: state => state.cities,
    statuses: state => state.statuses,
    sources: state => state.sources,
    error: state => state.error,
    params: (state, getters) => {
      return {
        search_string: getters.searchQuery,
        ...state.params,
        order_by: state.params.order.column,
        order_dir: state.params.order.desc ? 'desc' : 'asc',
      };
    },
    loading: state => state.loading,
    searchQuery: (state, getters, rootState, rootGetters) => rootGetters['util/searchQuery'],
    currentItem: state => state.currentItem,
  },
};
